import React, { useMemo, useState, memo, Component } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { HeadphonesIcon, ChevronRightIcon, StarIcon, TrendingUpIcon, UsersIcon, TagIcon, MicIcon, FilterIcon, LayoutListIcon, LayoutGridIcon, SearchIcon } from 'lucide-react';
import { useDarkMode } from '../context/DarkModeContext';
import { useDarkClasses } from '../hooks/useDarkClasses';
import { useRoleTheme } from '../hooks/useRoleTheme';
import { useUserRole } from '../context/UserRoleContext';
import { SearchWithAutocomplete, Suggestion } from '../components/UI/SearchWithAutocomplete';
import { audiobooks as realAudiobooks } from '../AudioBookDetails';
import { FilterSidebar, FilterSidebarWrapper, FilterSection, NavItem } from '../components/UI/FilterSidebar';
import { ProfileCard } from '../components/Directory/ProfileCard';
import { mapNarratorToProfileCard } from '../utils/profileCardMappers';
// Mock Narrator Data (expanded for the demo)
const narrators = [{
  id: '1',
  name: 'Sarah Johnson',
  avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
  bio: 'Award-winning narrator with over 100 audiobooks in her portfolio. Specializes in mystery and thriller genres.',
  expandedBio: 'Sarah Johnson is an Audie Award-winning narrator with a passion for storytelling. With over a decade of experience behind the mic, she has brought over 100 stories to life. Her ability to create distinct, memorable voices for large casts of characters makes her a favorite among mystery and thriller authors. When not narrating, she teaches voice acting workshops in Los Angeles.',
  genres: ['Mystery', 'Thriller', 'Romance'],
  rating: 4.9,
  booksNarrated: 127,
  availability: 'Available',
  style: 'Solo',
  voiceCharacteristics: ['Warm Tone', 'Versatile', 'Emotional Depth'],
  startingPrice: '$150/hr',
  location: 'Los Angeles, CA',
  verified: true,
  recentBooks: [{
    title: 'The Silent Echo',
    author: 'Elena Richards',
    cover: 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80'
  }, {
    title: 'Whispers in the Dark',
    author: 'Maya Rodriguez',
    cover: 'https://images.unsplash.com/photo-1543002588-bfa74002ed7e?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80'
  }, {
    title: 'Crimson Tide',
    author: 'James Patterson',
    cover: 'https://images.unsplash.com/photo-1532012197267-da84d127e765?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80'
  }]
}, {
  id: '2',
  name: 'Michael Harris',
  avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
  bio: 'Voice actor and narrator with a deep, resonant voice perfect for fantasy and sci-fi audiobooks.',
  expandedBio: 'Michael Harris brings epic worlds to life with his deep, resonant baritone. A classically trained actor, Michael specializes in high fantasy and space opera, where his command of gravitas and pacing shines. He has narrated for major publishing houses and independent authors alike, always delivering a performance that keeps listeners on the edge of their seats.',
  genres: ['Fantasy', 'Science Fiction', 'Horror'],
  rating: 4.8,
  booksNarrated: 95,
  availability: 'Booked',
  style: 'Duet',
  voiceCharacteristics: ['Deep & Resonant', 'Authoritative', 'Epic'],
  startingPrice: '$175/hr',
  location: 'New York, NY',
  verified: true,
  recentBooks: [{
    title: 'Beyond the Veil',
    author: 'Marcus Chen',
    cover: 'https://images.unsplash.com/photo-1621351183012-e2f9972dd9bf?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80'
  }, {
    title: 'Starship Frontiers',
    author: 'L.R. Hubbard',
    cover: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80'
  }, {
    title: 'The Dark Tower',
    author: 'Stephen King',
    cover: 'https://images.unsplash.com/photo-1518709268805-4e9042af9f23?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80'
  }]
}, {
  id: '3',
  name: 'Julia Whelan',
  avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
  bio: 'One of the most sought-after voices in audiobooks, known for her versatility and emotional depth.',
  expandedBio: "Julia Whelan is a writer, lifelong actor, and audiobook narrator of over 400 titles. She has won numerous awards, including Audies and Voice Arts Awards, and was named one of AudioFile Magazine's Golden Voices. Her performances are celebrated for their emotional intelligence, distinct character differentiations, and impeccable pacing.",
  genres: ['Romance', 'Literary Fiction', 'Contemporary'],
  rating: 5.0,
  booksNarrated: 400,
  availability: 'Available',
  style: 'Solo',
  voiceCharacteristics: ['Versatile', 'Character Voices', 'Clear Diction'],
  startingPrice: '$250/hr',
  location: 'Chicago, IL',
  verified: true,
  recentBooks: [{
    title: 'The Love Hypothesis',
    author: 'Ali Hazelwood',
    cover: 'https://images.unsplash.com/photo-1512820790803-83ca734da794?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80'
  }, {
    title: 'Book Lovers',
    author: 'Emily Henry',
    cover: 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80'
  }, {
    title: 'Educated',
    author: 'Tara Westover',
    cover: 'https://images.unsplash.com/photo-1589829085413-56de8ae18c73?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80'
  }]
}, {
  id: '4',
  name: 'Scott Brick',
  avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
  bio: 'A prolific narrator with a distinct voice, often narrating sci-fi, thrillers, and non-fiction.',
  expandedBio: 'Scott Brick has narrated over 800 audiobooks and won over 50 Earphones Awards. Known for his work in science fiction, thrillers, and non-fiction, Scott brings a level of intensity and intelligence to his narrations that listeners love. He has been named a Golden Voice by AudioFile Magazine and is a multi-Audie Award winner.',
  genres: ['Sci-Fi', 'Thriller', 'Non-Fiction'],
  rating: 4.7,
  booksNarrated: 800,
  availability: 'Available',
  style: 'Multi-cast',
  voiceCharacteristics: ['Intense', 'Intelligent', 'Distinctive'],
  startingPrice: '$225/hr',
  location: 'Los Angeles, CA',
  verified: true,
  recentBooks: [{
    title: 'Dune',
    author: 'Frank Herbert',
    cover: 'https://images.unsplash.com/photo-1541963463532-d68292c34b19?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80'
  }, {
    title: 'Foundation',
    author: 'Isaac Asimov',
    cover: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80'
  }, {
    title: 'Jurassic Park',
    author: 'Michael Crichton',
    cover: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80'
  }]
}];
// Genre configuration for quick links
const genreTiles = [{
  name: 'Romance',
  slug: 'romance'
}, {
  name: 'Fantasy',
  slug: 'fantasy'
}, {
  name: 'Sci-Fi',
  slug: 'sci-fi'
}, {
  name: 'Mystery',
  slug: 'mystery'
}, {
  name: 'Thriller',
  slug: 'thriller'
}, {
  name: 'Horror',
  slug: 'horror'
}, {
  name: 'Historical',
  slug: 'historical-fiction'
}, {
  name: 'Contemporary',
  slug: 'contemporary'
}];
// Build unified search suggestions (reused logic pattern)
const buildSearchSuggestions = (): Suggestion[] => {
  const suggestions: Suggestion[] = [];
  const seenAuthors = new Set<string>();
  const seenNarrators = new Set<string>();
  // Add narrators from our mock list first
  narrators.forEach(n => {
    suggestions.push({
      id: `narrator-${n.id}`,
      label: n.name,
      sublabel: 'Narrator',
      type: 'Narrator',
      image: n.avatar
    });
    seenNarrators.add(n.name);
  });
  realAudiobooks.forEach(book => {
    // Book titles
    suggestions.push({
      id: `book-${book.title}`,
      label: book.title,
      sublabel: `by ${book.author}`,
      type: 'Audiobook',
      image: book.coverImage
    });
    // Authors
    if (!seenAuthors.has(book.author)) {
      seenAuthors.add(book.author);
      suggestions.push({
        id: `author-${book.author}`,
        label: book.author,
        sublabel: 'Author',
        type: 'Author'
      });
    }
    // Narrators from real books if not already added
    book.narrators.forEach(narrator => {
      if (!seenNarrators.has(narrator)) {
        seenNarrators.add(narrator);
        suggestions.push({
          id: `narrator-${narrator}`,
          label: narrator,
          sublabel: 'Narrator',
          type: 'Narrator'
        });
      }
    });
  });
  return suggestions;
};
// Navigation Links for Sidebar
const navLinks: NavItem[] = [{
  label: 'Audiobooks',
  href: '/audiobooks',
  icon: HeadphonesIcon
}, {
  label: 'Narrators',
  href: '/narrators',
  icon: UsersIcon,
  isActive: true
}, {
  label: 'Genres',
  href: '/audiobooks/genres',
  icon: TagIcon
}, {
  label: 'New Releases',
  href: '/audiobooks/new',
  icon: StarIcon
}, {
  label: 'Bestsellers',
  href: '/audiobooks/bestsellers',
  icon: TrendingUpIcon
}];
const Narrators = () => {
  const {
    darkMode
  } = useDarkMode();
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const {
    isReader
  } = useUserRole();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const searchSuggestions = useMemo(() => buildSearchSuggestions(), []);
  // Filter States
  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
  const [selectedRatings, setSelectedRatings] = useState<string[]>([]);
  const [selectedAvailability, setSelectedAvailability] = useState<string[]>([]);
  const [selectedStyles, setSelectedStyles] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState('name-asc');
  // Sidebar States
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('grid');
  const handleSearchSelect = (suggestion: Suggestion) => {
    if (suggestion.type === 'Narrator') {
      const slug = suggestion.label.toLowerCase().replace(/\s+/g, '-');
      navigate(`/narrators/${slug}`);
    } else if (suggestion.type === 'Audiobook') {
      const slug = suggestion.label.replace(/\s+/g, '-').toLowerCase();
      navigate(`/book/${slug}`);
    } else if (suggestion.type === 'Author') {
      const slug = suggestion.label.toLowerCase().replace(/\s+/g, '-');
      navigate(`/author/${slug}`);
    } else {
      navigate(`/search?q=${encodeURIComponent(suggestion.label)}`);
    }
    setSearchQuery('');
  };
  const handleSearch = (query: string) => {
    navigate(`/search?q=${encodeURIComponent(query)}`);
  };
  const resetFilters = () => {
    setSelectedGenres([]);
    setSelectedRatings([]);
    setSelectedAvailability([]);
    setSelectedStyles([]);
    setSortBy('name-asc');
    setSearchQuery('');
  };
  const toggleMobileFilters = () => {
    setMobileFiltersOpen(!mobileFiltersOpen);
    document.body.style.overflow = !mobileFiltersOpen ? 'hidden' : 'auto';
  };
  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };
  // Filter Logic
  const filteredNarrators = useMemo(() => {
    let result = [...narrators];
    // Search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(n => n.name.toLowerCase().includes(query) || n.bio.toLowerCase().includes(query) || n.genres.some(g => g.toLowerCase().includes(query)));
    }
    // Genres
    if (selectedGenres.length > 0) {
      result = result.filter(n => n.genres.some(g => selectedGenres.includes(g)));
    }
    // Ratings
    if (selectedRatings.length > 0) {
      const minRating = Math.min(...selectedRatings.map(parseFloat));
      result = result.filter(n => n.rating >= minRating);
    }
    // Availability
    if (selectedAvailability.length > 0) {
      result = result.filter(n => selectedAvailability.includes(n.availability));
    }
    // Style
    if (selectedStyles.length > 0) {
      result = result.filter(n => selectedStyles.includes(n.style));
    }
    // Sort
    result.sort((a, b) => {
      switch (sortBy) {
        case 'name-asc':
          return a.name.localeCompare(b.name);
        case 'name-desc':
          return b.name.localeCompare(a.name);
        case 'books-desc':
          return b.booksNarrated - a.booksNarrated;
        case 'rating-desc':
          return b.rating - a.rating;
        default:
          return 0;
      }
    });
    return result;
  }, [searchQuery, selectedGenres, selectedRatings, selectedAvailability, selectedStyles, sortBy]);
  // Filter Configuration
  const filterSections: FilterSection[] = [{
    id: 'sort',
    title: 'Sort By',
    type: 'radio',
    options: [{
      value: 'name-asc',
      label: 'A-Z'
    }, {
      value: 'name-desc',
      label: 'Z-A'
    }, {
      value: 'books-desc',
      label: 'Most Books'
    }, {
      value: 'rating-desc',
      label: 'Highest Rated'
    }],
    value: sortBy,
    onChange: setSortBy
  }, {
    id: 'genres',
    title: 'Genres',
    type: 'checkbox',
    options: genreTiles.map(g => ({
      value: g.name,
      label: g.name
    })),
    value: selectedGenres,
    onChange: (val: string) => setSelectedGenres(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val])
  },
  // Availability — vendor/author mode only
  ...(!isReader ? [{
    id: 'availability',
    title: 'Availability',
    type: 'checkbox' as const,
    options: [{
      value: 'Available',
      label: 'Available for Projects'
    }, {
      value: 'Booked',
      label: 'Currently Booked'
    }],
    value: selectedAvailability,
    onChange: (val: string) => setSelectedAvailability(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val])
  }] : []), {
    id: 'style',
    title: 'Narration Style',
    type: 'checkbox',
    options: [{
      value: 'Solo',
      label: 'Solo Narration'
    }, {
      value: 'Duet',
      label: 'Duet Narration'
    }, {
      value: 'Multi-cast',
      label: 'Multi-cast'
    }],
    value: selectedStyles,
    onChange: (val: string) => setSelectedStyles(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val])
  }, {
    id: 'ratings',
    title: 'Rating',
    type: 'checkbox',
    options: [{
      value: '4.5',
      label: '4.5+ Stars'
    }, {
      value: '4.0',
      label: '4.0+ Stars'
    }],
    value: selectedRatings,
    onChange: (val: string) => setSelectedRatings(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val])
  }];
  return <div className={`min-h-screen ${dk.page}`} data-id="element-11904" data-mp="sdsz9spbq0qt">
      {/* Header with Search */}
      <div className={`py-10 ${dk.surface} border-b ${dk.border}`} data-id="element-11905" data-mp="sbowje35v7s4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-11906" data-mp="3ru7pp97eh3nw">
          {/* Breadcrumb */}
          <div className={`flex items-center text-sm mb-4 ${dk.textMuted}`} data-id="element-11907" data-mp="nof9v5h7fq4">
            <Link to="/" className={`hover:${dk.textPrimary} transition-colors`} data-id="element-11908" data-mp="4noz5vbcgj4k">
              Home
            </Link>
            <ChevronRightIcon className="h-4 w-4 mx-2" data-id="element-11909" data-mp="9grsc2c7lqk8" />
            <Link to="/audiobooks" className={`hover:${dk.textPrimary} transition-colors`} data-id="element-11910" data-mp="391dij1g0qfu8">
              Audiobooks
            </Link>
            <ChevronRightIcon className="h-4 w-4 mx-2" data-id="element-11911" data-mp="35l0z5jzdtfj5" />
            <span className={dk.textPrimary} data-id="element-11912" data-mp="35ierlkchr00x">Narrators</span>
          </div>

          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6" data-id="element-11913" data-mp="30lxu3zjej9b6">
            <div className="flex items-center gap-3" data-id="element-11914" data-mp="2wihwazvlj2ww">
              <MicIcon className={`h-8 w-8 ${rt.textPrimary}`} data-id="element-11915" data-mp="2rkzyo5n14a47" />
              <div data-id="element-11916" data-mp="2rkgd8ey673lf">
                <h1 className={`text-3xl font-bold ${dk.textHeading}`} data-id="element-11917" data-mp="2mln1aqyj7knd">
                  Audiobook Narrators
                </h1>
                <p className={`mt-1 ${dk.textSecondary}`} data-id="element-11918" data-mp="2huoi9jm5s8gf">
                  Discover the voices that bring your favorite stories to life
                </p>
              </div>
            </div>

            {/* Unified Search */}
            <div className="w-full md:max-w-md" data-id="element-11919" data-mp="28vtcg8n110dn">
              <SearchWithAutocomplete value={searchQuery} onChange={setSearchQuery} suggestions={searchSuggestions} placeholder="Search narrators, books, authors..." variant="rounded" onSelect={handleSearchSelect} onSearch={handleSearch} data-id="element-11920" data-mp="23uvunx5xi8co" />
            </div>
          </div>
        </div>
      </div>

      {/* Main Content: Filters and Narrators list */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-11921" data-mp="3duet7pchad1o">
        {/* Mobile Filter Button & View Toggle */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6 lg:hidden" data-id="element-11922" data-mp="342ityaynogvv">
          <button className={`flex-1 flex items-center justify-center px-4 py-2 border rounded-lg shadow-sm ${dk.card} ${dk.textSecondary} hover:${dk.surfaceMuted}`} onClick={toggleMobileFilters} data-id="element-11923" data-mp="34067bkhydich">
            <FilterIcon className="h-5 w-5 mr-2" data-id="element-11924" data-mp="2z2o3g8isltbc" />
            Filters & Menu
          </button>
          <div className={`flex items-center border rounded-lg p-1 self-start ${dk.card}`} data-id="element-11925" data-mp="2l2v5bs4xsatc">
            <button onClick={() => setViewMode('list')} className={`p-1.5 rounded transition-colors ${viewMode === 'list' ? `${rt.bgDark} text-white` : `${dk.textMuted} hover:${dk.textSecondary}`}`} data-id="element-11926" data-mp="2q2rb9wx72cbj">
              <LayoutListIcon className="h-5 w-5" data-id="element-11927" data-mp="21jpkf3uvdxdk" />
            </button>
            <button onClick={() => setViewMode('grid')} className={`p-1.5 rounded transition-colors ${viewMode === 'grid' ? `${rt.bgDark} text-white` : `${dk.textMuted} hover:${dk.textSecondary}`}`} data-id="element-11928" data-mp="2gfnd2c5l4jl1">
              <LayoutGridIcon className="h-5 w-5" data-id="element-11929" data-mp="2pd7fhof6olx8" />
            </button>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-8" data-id="element-11930" data-mp="2kemmoa29h4it">
          {/* Sidebar filters */}
          <FilterSidebarWrapper isOpen={mobileFiltersOpen} onClose={toggleMobileFilters} isCollapsed={sidebarCollapsed} onToggleCollapse={toggleSidebar} data-id="element-11931" data-mp="2z77secgpivgi">
            <FilterSidebar navigation={navLinks} sections={filterSections} onReset={resetFilters} data-id="element-11932" data-mp="344gego4z7jn1" />
          </FilterSidebarWrapper>

          {/* Main Narrators grid/list */}
          <div className="flex-1 min-w-0" data-id="element-11933" data-mp="3bowkmlvqxvl3">
            {/* Desktop View Toggle */}
            <div className="hidden lg:flex justify-end mb-6" data-id="element-11934" data-mp="3poebr7wilkxm">
              <div className={`flex items-center border rounded-lg p-1 ${dk.card}`} data-id="element-11935" data-mp="3pls1v1m5itc8">
                <button onClick={() => setViewMode('list')} className={`p-1.5 rounded transition-colors ${viewMode === 'list' ? `${rt.bgDark} text-white` : `${dk.textMuted} hover:${dk.textPrimary}`}`} aria-label="List view" data-id="element-11936" data-mp="2wz72pezfpcy4">
                  <LayoutListIcon className="h-5 w-5" data-id="element-11937" data-mp="31ya7qzfezj63" />
                </button>
                <button onClick={() => setViewMode('grid')} className={`p-1.5 rounded transition-colors ${viewMode === 'grid' ? `${rt.bgDark} text-white` : `${dk.textMuted} hover:${dk.textPrimary}`}`} aria-label="Grid view" data-id="element-11938" data-mp="31w79js43yjqa">
                  <LayoutGridIcon className="h-5 w-5" data-id="element-11939" data-mp="ophbre3e8dds" />
                </button>
              </div>
            </div>

            {/* List or Grid of narrators, filtered */}
            <div className={`grid ${viewMode === 'grid' ? 'grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6' : 'grid-cols-1 gap-4'}`} data-id="element-11940" data-mp="fs6yxoaxgg6r">
              {filteredNarrators.length > 0 ? filteredNarrators.map(narrator => {
              const profileData = mapNarratorToProfileCard(narrator);
              return <ProfileCard key={narrator.id} data={profileData} viewMode={viewMode} data-id="element-11941" data-mp="yi6la3fdvh6h" />;
            }) : <div className={`col-span-full text-center py-12 rounded-xl shadow-sm border ${dk.card}`} data-id="element-11942" data-mp="3j6b4soly7g09">
                  <div className={`mx-auto w-12 h-12 rounded-full flex items-center justify-center mb-4 ${dk.surfaceMuted}`} data-id="element-11943" data-mp="344cpcvgccw14">
                    <SearchIcon className="h-6 w-6 text-gray-400" data-id="element-11944" data-mp="3053ybycorc99" />
                  </div>
                  <h3 className={`text-lg font-medium ${dk.textHeading}`} data-id="element-11945" data-mp="2vag7vty4g7s1">
                    No narrators found
                  </h3>
                  <p className={`mt-1 ${dk.textSecondary}`} data-id="element-11946" data-mp="2ldv1b46geom3">
                    Try adjusting your filters or search terms
                  </p>
                  <button onClick={resetFilters} className={`mt-4 font-medium ${rt.accentLink}`} data-id="element-11947" data-mp="2ge8kkkrf2q8j">
                    Clear all filters
                  </button>
                </div>}
            </div>
          </div>
        </div>
      </div>
    </div>;
};
export default Narrators;